/**
 * Predictive probe-failure rules. Pure functions over recent temperature
 * samples. Used by both the mobile UI (live "warning" badges) and the
 * server-side `probe-rules` cron edge function (writes `probe_alert` events).
 */
import { z } from 'zod';

export const ProbeSampleSchema = z.object({
  ts: z.string().datetime(),
  temperature_c: z.number(),
});
export type ProbeSample = z.infer<typeof ProbeSampleSchema>;

export const RuleSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type RuleSeverity = z.infer<typeof RuleSeveritySchema>;

export interface RuleHit {
  rule_id: string;
  severity: RuleSeverity;
  message: string;
  window_start: string;
  window_end: string;
  samples_count: number;
}

export interface RuleContext {
  /** Storage unit's configured min/max in Celsius. */
  target_min_c: number | null;
  target_max_c: number | null;
  /** Window of recent readings (oldest → newest). */
  samples: ProbeSample[];
  /**
   * T-220: whether the storage unit has an automatic probe linked (probes row
   * active, not archived, protocol != manual). `false` disables ruleStaleProbe:
   * a fridge checked manually has no probe that can go "silent". `undefined`
   * keeps the legacy behaviour (callers that don't know).
   */
  has_linked_probe?: boolean;
  /**
   * T-220: readings coming from automatic probes only (source ble/mqtt).
   * When provided, ruleStaleProbe measures staleness on these instead of all
   * samples, so a manual reading doesn't arm/reset the "sonda silente" timer.
   */
  probe_samples?: ProbeSample[];
}

const HOUR = 3_600_000;

/** Rule R1: any sample outside [min,max] for >15 min sustained → critical. */
export function ruleSustainedOutOfRange(ctx: RuleContext): RuleHit | null {
  if (ctx.target_min_c == null && ctx.target_max_c == null) return null;
  if (ctx.samples.length < 2) return null;
  let breachStart: string | null = null;
  let breachEnd: string | null = null;
  let count = 0;
  for (const s of ctx.samples) {
    const high = ctx.target_max_c != null && s.temperature_c > ctx.target_max_c;
    const low = ctx.target_min_c != null && s.temperature_c < ctx.target_min_c;
    if (high || low) {
      breachStart ??= s.ts;
      breachEnd = s.ts;
      count++;
    } else {
      if (breachStart && breachEnd && Date.parse(breachEnd) - Date.parse(breachStart) >= 15 * 60_000) break;
      breachStart = null;
      breachEnd = null;
      count = 0;
    }
  }
  if (!breachStart || !breachEnd) return null;
  const durationMin = (Date.parse(breachEnd) - Date.parse(breachStart)) / 60_000;
  if (durationMin < 15) return null;
  return {
    rule_id: 'sustained_out_of_range',
    severity: 'critical',
    message: `Temperatura fuori intervallo per ${Math.round(durationMin)} min`,
    window_start: breachStart,
    window_end: breachEnd,
    samples_count: count,
  };
}

/** Rule R2: rising trend (>0.5°C/h over last 6h) on a positive cold storage → warning. */
export function ruleRisingTrend(ctx: RuleContext): RuleHit | null {
  if (ctx.target_max_c == null || ctx.target_max_c >= 15) return null;
  const cutoff = Date.now() - 6 * HOUR;
  const recent = ctx.samples.filter((s) => Date.parse(s.ts) >= cutoff);
  if (recent.length < 6) return null;
  const first = recent[0];
  const last = recent[recent.length - 1];
  const dtH = (Date.parse(last.ts) - Date.parse(first.ts)) / HOUR;
  if (dtH < 1) return null;
  const slope = (last.temperature_c - first.temperature_c) / dtH;
  if (slope <= 0.5) return null;
  return {
    rule_id: 'rising_trend',
    severity: 'warning',
    message: `Temperatura in salita ${slope.toFixed(2)} °C/h`,
    window_start: first.ts,
    window_end: last.ts,
    samples_count: recent.length,
  };
}

/** Rule R3: gap > 2× expected interval (probe disconnected) → warning. */
export function ruleStaleProbe(ctx: RuleContext, expectedIntervalMin = 30): RuleHit | null {
  // T-220: senza sonda automatica associata non esiste una "sonda silente" —
  // i frigoriferi a rilevazione manuale non devono mai generare questo alert.
  if (ctx.has_linked_probe === false) return null;
  const src = ctx.probe_samples ?? ctx.samples;
  if (src.length === 0) return null;
  const last = src[src.length - 1];
  const ageMin = (Date.now() - Date.parse(last.ts)) / 60_000;
  if (ageMin < expectedIntervalMin * 2) return null;
  return {
    rule_id: 'stale_probe',
    severity: 'warning',
    message: `Sonda silente da ${Math.round(ageMin)} min`,
    window_start: last.ts,
    window_end: new Date().toISOString(),
    samples_count: 0,
  };
}

/** Rule R4: high variance (stddev > 2°C in last hour) → info, possibili apertura/chiusura. */
export function ruleHighVariance(ctx: RuleContext): RuleHit | null {
  const cutoff = Date.now() - HOUR;
  const recent = ctx.samples.filter((s) => Date.parse(s.ts) >= cutoff);
  if (recent.length < 4) return null;
  const mean = recent.reduce((a, b) => a + b.temperature_c, 0) / recent.length;
  const variance = recent.reduce((a, b) => a + (b.temperature_c - mean) ** 2, 0) / recent.length;
  const stddev = Math.sqrt(variance);
  if (stddev <= 2) return null;
  return {
    rule_id: 'high_variance',
    severity: 'info',
    message: `Oscillazioni elevate (σ ${stddev.toFixed(2)} °C)`,
    window_start: recent[0].ts,
    window_end: recent[recent.length - 1].ts,
    samples_count: recent.length,
  };
}

const ALL_RULES = [
  ruleSustainedOutOfRange,
  ruleRisingTrend,
  ruleStaleProbe,
  ruleHighVariance,
];

export function evaluateProbe(ctx: RuleContext): RuleHit[] {
  return ALL_RULES.map((r) => r(ctx)).filter((x): x is RuleHit => x !== null);
}
